/**
 * メール送信 API スキーマ
 *
 * agent-core（Deferred Tool） / desktop-app（Electron クライアント） / infra（Lambda）
 * 間でリクエスト・レスポンス・添付ファイルの形状を共有する SSOT。
 *
 *  - GET  /email/send : EmailSendingAvailabilityResponseSchema
 *  - POST /email/send : SendEmailRequestSchema → SendEmailResponseSchema
 *
 * 送信先ドメインの許可判定はワークスペース設定に依存せず、実行環境（インフラ）側が
 * 唯一の判定基準として持つ。ここでは入出力の形状のみを定義する。
 */
import { z } from 'zod';
// ========================================
// GET /email/send: キャパビリティ
// ========================================
/**
 * その実行環境でメール送信が成立するか。
 *
 * 「バックエンドに到達できるか」ではなく「送信元アドレスと送信先許可ドメインが設定済みか」を表す。
 * どちらかが未設定の環境では全送信が拒否されるため、設定 UI はここが false のとき
 * 有効化しても無意味であることを利用者へ提示する。
 */
export const EmailSendingAvailabilityResponseSchema = z.object({
    available: z.boolean(),
});
// ========================================
// 送信先ドメイン許可リストの正規化（infra/lib の CDK 設定と infra/lambda の実行時判定で共有し、
// 「.env に書いた値」と「Lambda が判定に使う値」がずれないようにする）
// ========================================
/**
 * カンマ区切りのドメイン列を正規化する（trim + 小文字化 + 空要素除去）。
 */
export function parseEmailDomainList(value) {
    if (!value)
        return [];
    return value
        .split(',')
        .map((d) => d.trim().toLowerCase())
        .filter((d) => d.length > 0);
}
// ========================================
// 危険とみなすファイル拡張子（agent-core のサンドボックス提出・メール添付の両方で共有し、
// denylist の防御範囲がずれないようにする）
// ========================================
/**
 * 添付・提出を拒否する拡張子（小文字、ドット付き）。
 *
 * これは「事故で実行形式を送ってしまう」ことを防ぐ**補助的な**ガードであり、
 * 意図的な回避に耐える境界ではない（`report.exe.` のように末尾を変える、
 * ここに載っていない実行形式を使う、といった抜け道が原理的に残る）。
 */
export const DANGEROUS_FILE_EXTENSIONS = [
    '.exe',
    '.dll',
    '.bat',
    '.cmd',
    '.com',
    '.scr',
    '.msi',
    '.lnk',
    '.pif',
    '.hta',
    '.ps1',
    '.sh',
    '.vbs',
    '.vbe',
    '.wsf',
    '.js',
    '.jar',
    '.reg',
];
// ========================================
// 添付ファイル制約（agent-core / infra 間で共有し、多層防御の基準を一致させる）
// ========================================
export const EMAIL_ATTACHMENT_LIMITS = {
    /**
     * 添付ファイル合計サイズの上限（バイト、base64 化前の元サイズ基準）
     *
     * 上限を決めているのは API Gateway のペイロード上限（10MB）ではなく、その先の
     * **Lambda 同期呼び出しのイベントペイロード上限 6MB** である（こちらの方が厳しい）。
     * 添付は base64（約 4/3 倍）で JSON ボディに載るため、生 4MB → 約 5.33MB となり、
     * 本文・件名・ヘッダ・JSON 構造のオーバーヘッドを加えても 6MB に収まる。
     *
     * 生 6MB を許すと base64 で約 8MB になり Lambda の上限を超える。その場合 API Gateway が
     * スキーマ外のエラーを返し、クライアントはそれを `backend-unavailable`（到達性の問題）へ
     * 丸めてしまうため、利用者にはサイズ超過が原因だと分からない失敗になる。
     */
    MAX_TOTAL_SIZE_BYTES: 4 * 1024 * 1024,
    /** 1回の送信あたりに添付できるファイル数の上限 */
    MAX_COUNT: 10,
    /**
     * 添付を拒否する拡張子（`DANGEROUS_FILE_EXTENSIONS` 参照）。
     * 受信側のメールゲートウェイ・エンドポイント保護が本来の防御線であり、
     * ここを allowlist 化しない限り列挙の網羅性を前提にしないこと。
     */
    BLOCKED_EXTENSIONS: DANGEROUS_FILE_EXTENSIONS,
};
// ========================================
// 本文・件名の長さ制約
// ========================================
/**
 * 件名・本文の長さ上限。
 *
 * 添付だけを `EMAIL_ATTACHMENT_LIMITS` で制限しても、本文が無制限だと Lambda 同期呼び出しの
 * イベントペイロード上限 6MB を本文単体で超えられてしまう。その場合 API Gateway が
 * スキーマ外のエラーを返し、クライアントは `backend-unavailable`（到達性の問題）へ丸めるため、
 * 利用者には「本文が長すぎる」ことが伝わらない失敗になる。添付 4MB（base64 で約 5.33MB）を
 * 使い切った場合でも残余に収まるよう、本文はそれより十分小さい値で切る。
 */
export const EMAIL_CONTENT_LIMITS = {
    /** 件名の最大文字数（RFC 5322 のヘッダ行長を大きく下回る実用上限） */
    MAX_SUBJECT_LENGTH: 512,
    /**
     * 本文の最大文字数。
     * 最悪ケース（全文が3バイトの日本語文字）でも約 300KB で、添付上限と併用しても 6MB に収まる。
     * サニタイザは `data:` スキームを許可しないため、本文へ画像を埋め込む用途は想定しない。
     */
    MAX_BODY_LENGTH: 100_000,
};
// ========================================
// 添付ファイル
// ========================================
export const EmailAttachmentSchema = z.object({
    name: z.string().trim().min(1).describe('添付ファイル名'),
    mimeType: z.string().trim().min(1).describe('添付ファイルの MIME タイプ'),
    /**
     * 参考値。クライアントの自己申告であり、サーバー側のサイズ判定には使わない
     * （Lambda は `contentBase64` の実体から算出する。email-service の actualSizeBytes 参照）。
     * ログ・UI 表示用にのみ扱うこと。
     */
    sizeBytes: z
        .number()
        .int()
        .nonnegative()
        .describe('添付ファイルのサイズ（バイト、base64 化前の元サイズ。参考値・検証には使わない）'),
    contentBase64: z.string().min(1).describe('添付ファイル本体（base64 エンコード済み）'),
});
// ========================================
// POST /email/send: リクエスト
// ========================================
export const EmailBodyFormatSchema = z.enum(['text', 'html']);
export const SendEmailRequestSchema = z.object({
    to: z.array(z.string().email()).min(1).describe('宛先メールアドレス（1件以上）'),
    cc: z.array(z.string().email()).optional().default([]).describe('CC 宛先メールアドレス'),
    bcc: z.array(z.string().email()).optional().default([]).describe('BCC 宛先メールアドレス'),
    subject: z.string().min(1).max(EMAIL_CONTENT_LIMITS.MAX_SUBJECT_LENGTH).describe('件名'),
    body: z.string().min(1).max(EMAIL_CONTENT_LIMITS.MAX_BODY_LENGTH).describe('本文'),
    bodyFormat: EmailBodyFormatSchema.optional()
        .default('text')
        .describe('本文形式（既定: プレーンテキスト）'),
    attachments: z.array(EmailAttachmentSchema).optional().default([]).describe('添付ファイル一覧'),
});
// ========================================
// POST /email/send: レスポンス
// ========================================
export const SendEmailRejectReasonSchema = z.enum([
    'domain-not-allowed',
    'validation-error',
    'attachment-too-large',
    'recipient-not-verified',
    'send-failed',
    'backend-unavailable',
]);
export const SendEmailSuccessResponseSchema = z.object({
    status: z.literal('sent'),
    recipients: z.array(z.string()),
    attachedFileNames: z.array(z.string()),
    messageId: z.string(),
});
export const SendEmailRejectedResponseSchema = z.object({
    status: z.literal('rejected'),
    reason: SendEmailRejectReasonSchema,
    message: z.string(),
});
export const SendEmailResponseSchema = z.discriminatedUnion('status', [
    SendEmailSuccessResponseSchema,
    SendEmailRejectedResponseSchema,
]);
//# sourceMappingURL=email-schemas.js.map